"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Suite = {
  number: string;
  numeral: string;
  name: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
  detailImage: string;
  detailSrcset: string;
  detailSizes: string;
  detailAlt: string;
  pieces: string;
  piece1: string;
  piece2: string;
  piece3: string;
  narrative: string;
};
const suites: Suite[] = [{
  number: "01",
  numeral: "No. I",
  name: "Summer Camp Highs",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701369/img/tribe_camp_fire.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701369/img/tribe_camp_fire.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701369/img/tribe_camp_fire.png-wvc-sizes",
  alt: "Youth Tribe members gathered around a campfire during summer camp",
  detailImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701369/img/detail_camp_badge.png",
  detailSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701369/img/detail_camp_badge.png-wvc-srcset",
  detailSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701369/img/detail_camp_badge.png-wvc-sizes",
  detailAlt: "Youth Tribe camp merit badge detail",
  pieces: "Core pillars of our summer experience",
  piece1: "Outdoor wilderness survival training",
  piece2: "Team-building leadership workshops",
  piece3: "Nightly campfire storytelling sessions",
  narrative: "Our annual summer camp is where the Tribe truly bonds. Away from the screens and city noise, we build character through shared challenges, late-night laughter, and the quiet magic of the great outdoors."
}, {
  number: "02",
  numeral: "No. II",
  name: "Team Sports Arena",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701369/img/tribe_sports_field.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701369/img/tribe_sports_field.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701369/img/tribe_sports_field.png-wvc-sizes",
  alt: "Youth Tribe members playing competitive team sports on a field",
  detailImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701369/img/detail_sports_ball.png",
  detailSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701369/img/detail_sports_ball.png-wvc-srcset",
  detailSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701369/img/detail_sports_ball.png-wvc-sizes",
  detailAlt: "Sports equipment detail",
  pieces: "Building resilience through competition",
  piece1: "Weekly inter-group soccer tournaments",
  piece2: "High-intensity basketball drills",
  piece3: "Collaborative relay race challenges",
  narrative: "Sports at Youth Tribe aren't just about winning; they are about showing up for your teammates. Whether on the court or the field, we emphasize sportsmanship, grit, and collective growth."
}, {
  number: "03",
  numeral: "No. III",
  name: "Live Music Nights",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701369/img/tribe_music_stage.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701369/img/tribe_music_stage.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701369/img/tribe_music_stage.webp-wvc-sizes",
  alt: "Youth Tribe members performing live music on stage",
  detailImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701369/img/detail_music_mic.png",
  detailSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701369/img/detail_music_mic.png-wvc-srcset",
  detailSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701369/img/detail_music_mic.png-wvc-sizes",
  detailAlt: "Microphone and stage equipment detail",
  pieces: "Amplifying young voices and talent",
  piece1: "Open mic sessions for aspiring artists",
  piece2: "Live band performances by youth members",
  piece3: "Collaborative songwriting workshops",
  narrative: "Our stage is a safe space for expression. From acoustic sets to high-energy band performances, we celebrate the creative spirit of our members and provide a platform for their unique voices."
}];
function SuiteBlock({
  suite,
  index
}: {
  suite: Suite;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const isOdd = index % 2 === 0;
  return <div ref={ref} data-index={index} className={`relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      <div className={`relative lg:col-span-7 ${isOdd ? "lg:order-1" : "lg:order-2"}`}>
        <div className="relative overflow-hidden rounded-lg">
          <img src={suite.image} data-wvc-srcset={suite.srcset} data-wvc-sizes={suite.sizes} alt={suite.alt} loading="lazy" decoding="async" className="w-full h-[480px] md:h-[600px] lg:h-[680px] object-cover" />
          <div className="absolute inset-0 bg-primary/10 mix-blend-multiply pointer-events-none" />
        </div>

        <div className={`hidden md:block absolute ${isOdd ? "-right-6 lg:-right-10 bottom-16" : "-left-6 lg:-left-10 bottom-16"} w-36 h-36 lg:w-44 lg:h-44 overflow-hidden rounded-lg shadow-lg ring-1 ring-border`}>
          <img src={suite.detailImage} data-wvc-srcset={suite.detailSrcset} data-wvc-sizes={suite.detailSizes} alt={suite.detailAlt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
        </div>
      </div>

      <div className={`lg:col-span-5 flex flex-col justify-center ${isOdd ? "lg:order-2" : "lg:order-1"}`}>
        <div className="font-default text-[64px] md:text-[80px] leading-none text-foreground/0 mb-6" style={{
        WebkitTextStroke: "1px var(--primary)"
      }}>
          {suite.numeral}
        </div>

        <h3 className="font-default font-bold text-3xl md:text-4xl lg:text-[44px] leading-tight text-foreground mb-8">
          {suite.name}
        </h3>

        <div className="font-mono text-xs tracking-[0.25em] uppercase text-primary mb-3">
          Program Highlights
        </div>
        <p className="font-default text-base text-foreground mb-5">
          {suite.pieces}
        </p>

        <ul className="space-y-2.5 mb-8">
          <li className="font-default text-base text-foreground flex gap-3">
            <span className="text-primary mt-2 shrink-0">
              <span className="block w-1.5 h-1.5 bg-primary" />
            </span>
            <span>{suite.piece1}</span>
          </li>
          <li className="font-default text-base text-foreground flex gap-3">
            <span className="text-primary mt-2 shrink-0">
              <span className="block w-1.5 h-1.5 bg-primary" />
            </span>
            <span>{suite.piece2}</span>
          </li>
          <li className="font-default text-base text-foreground flex gap-3">
            <span className="text-primary mt-2 shrink-0">
              <span className="block w-1.5 h-1.5 bg-primary" />
            </span>
            <span>{suite.piece3}</span>
          </li>
        </ul>

        <p className="font-default text-[17px] leading-relaxed text-foreground/85 mb-10 max-w-xl">
          {suite.narrative}
        </p>

        <div className="flex flex-wrap items-center gap-6">
          <Link to="/OurRhythmPage" className="group inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-primary">
            <span className="relative">
              Learn more
              <span className="absolute left-0 -bottom-1 h-px w-full bg-primary origin-left scale-x-100 group-hover:scale-x-0 transition-transform duration-500 ease-out" />
            </span>
            <ArrowRight className="size-3.5 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>

          <Button asChild size="lg" className="px-7 h-11 font-mono text-xs tracking-[0.2em] uppercase bg-primary text-white hover:bg-primary/90">
            <Link to="/ParentHubPage">Register Now</Link>
          </Button>
        </div>
      </div>
    </div>;
}
export default function WeeklyRhythm() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="5009" id="weekly-rhythm" className="bg-background text-foreground pt-[100px] pb-32 md:pb-40 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-24 md:mb-32 transition-all duration-1000 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-12">
            <div className="font-mono text-xs tracking-[0.3em] uppercase text-primary mb-8">
              Youth Tribe — Weekly Rhythm
            </div>
            <h1 className="font-default font-black text-5xl sm:text-6xl md:text-7xl lg:text-[96px] xl:text-[120px] leading-[0.95] tracking-tighter text-foreground mb-10">
              Community,
              <br />
              <span className="text-primary">in motion.</span>
            </h1>
            <p className="font-default text-lg md:text-[19px] leading-relaxed text-foreground/80 max-w-[540px]">
              Youth Tribe is a dynamic space for connection, growth, and high-energy fun. From summer camp adventures to weekly social nights, we provide the environment for the next generation to thrive together.
            </p>
          </div>
        </div>

        <div className="space-y-32 md:space-y-44 lg:space-y-[180px]">
          {suites.map((suite, i) => <div key={suite.number} data-index={i}>
              <SuiteBlock suite={suite} index={i} />
              {i < suites.length - 1 && <div className="mt-32 md:mt-44 lg:mt-[180px] -mb-32 md:-mb-44 lg:-mb-[180px]">
                  <Separator className="bg-border" />
                </div>}
            </div>)}
        </div>
      </div>
    </section>;
}